import { useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { useTheme, themes } from '@/contexts/ThemeContext';
import { useRealtimeSync } from '@/hooks/useRealtimeSync';
import { usePageTracker } from '@/hooks/useAnalytics';
import api from '@/lib/api';
import { useBootstrap } from '@/lib/useBootstrap';
import { hasBdAccess } from '@/lib/roles';
import { AdminOnlyMark, useAdminHighlight } from '@/components/AdminOnlyMark';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  LayoutDashboard, Briefcase, Users, Building2,
  Calendar, CalendarDays, Menu, LogOut, Settings, FileBarChart,
  GraduationCap, Globe, Send, Contact, Bot,
  Search, PanelLeftClose, PanelLeft, Wrench, Handshake,
  ChevronDown, Palette, BarChart3, Zap, Activity, Phone,
  Shield, Lock, Inbox, BookOpenCheck, LayoutGrid, Mail, Bell, Sparkles, Crown, Gauge, IndianRupee, Waves,
  Target, ListChecks, SlidersHorizontal,
} from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import { DensityToggle } from '@/components/DensityToggle';
import WhatsNewButton from '@/components/WhatsNewButton';
import { GuidedTour } from '@/components/GuidedTour';
import { CommandPalette } from '@/components/CommandPalette';
import { TopLoader } from '@/components/TopLoader';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ViewAsSwitcher, ViewAsBanner } from '@/components/ViewAsRole';
import { AiStatusBanner } from '@/components/AiStatusBanner';
import { WorkspaceSwitcher } from '@/components/WorkspaceSwitcher';
import { BrandMark, BrandHorizontal } from '@/components/Brand';
import { ThemeAmbience } from '@/components/ThemeAmbience';


/* ── Page Transition Wrapper ── */
function AnimatedPage({ children, fill = false }) {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [transitioning, setTransitioning] = useState(false);
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== prevPath.current) {
      setTransitioning(true);
      const t = setTimeout(() => {
        setDisplayChildren(children);
        setTransitioning(false);
        prevPath.current = location.pathname;
      }, 120);
      return () => clearTimeout(t);
    } else {
      setDisplayChildren(children);
    }
  }, [children, location.pathname]);

  return (
    <div
      className={`${transitioning ? 'page-exit' : 'page-enter'} ${
        fill ? 'page-fill flex flex-col' : ''
      }`}
    >
      {displayChildren}
    </div>
  );
}

/** Flip to true to offer all 19 themes in the picker again (nothing deleted). */
const SHOW_ALL_THEMES = false;

/**
 * Phase 2 — content width by page type (tokens: --w-data / --w-dash / --w-read).
 * Reading and form screens get a narrow column so lines stay readable; data
 * screens stay wide but stop stretching on very large monitors.
 */
const READ_WIDTH = [
  '/app/profile', '/app/settings', '/app/admin', '/app/learning', '/app/whats-new',
  '/app/notifications', '/app/billing',
];
const DASH_WIDTH = [
  '/app/dashboard', '/app/owner-dashboard', '/app/reports', '/app/product-analytics',
  '/app/marketing-analytics', '/app/scoring-engine',
];
const startsWithAny = (path, list) => list.some(p => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`));
/**
 * Pages that own the whole viewport: their list scrolls, the page does not.
 * They get a DEFINITE height from the shell (main -> .page-frame -> .page-fill)
 * instead of guessing `calc(100vh - 7rem)`, so a banner appearing above them,
 * a 125%-scaled Windows laptop and a Mac notch are all handled for free.
 * Exact paths only — /app/candidates/:id is a normal scrolling page.
 */
const FILL_ROUTES = new Set(['/app/candidates']);
function pageWidthFor(pathname) {
  if (startsWithAny(pathname, READ_WIDTH)) return 'max-w-read';
  if (startsWithAny(pathname, DASH_WIDTH)) return 'max-w-dash';
  return 'max-w-data';
}



// Feature-flag in-memory cache (5 min TTL)
let _ffCache = null;
let _ffExpiry = 0;
async function getFeatureFlags() {
  if (_ffCache && Date.now() < _ffExpiry) return _ffCache;
  const res = await api.get('/settings/feature-flags');
  _ffCache = res.data;
  _ffExpiry = Date.now() + 5 * 60 * 1000;
  return res.data;
}

const navItems = [
  /* ── WORK: the recruiter's day. Everyone starts here. ─────────────────── */
  { to: '/app/dashboard', icon: LayoutDashboard, label: 'Dashboard', hideBde: true },
  // A BDE's day used to start on a tab inside Business Dev. They now get their
  // own Dashboard row (owner, 6 Jun 2026) — same shape as the recruiter one,
  // measured on BD work. Directors reach it from Business Dev → Team.
  { to: '/app/bd-dashboard', icon: LayoutDashboard, label: 'Dashboard', bdeOnly: true },
  { to: '/app/jobs', icon: Briefcase, label: 'Jobs', hideBde: true },
  { to: '/app/candidates', icon: Users, label: 'Talent Hub', hideBde: true },
  // HiroMatch = HiroApply + HiroPool, out of Talent Hub and onto its own row
  // (owner, 6 Jun 2026): reviewing the candidates the engine puts in front of
  // you is a recruiter's main daily job, not a sourcing sub-tab.
  { to: '/app/hiromatch', icon: Sparkles, label: 'HiroMatch', hideBde: true },
  // AI Recruiter: admins, directors, and managers a Director/Admin switched on.
  { to: '/app/ai-recruit', icon: Bot, label: 'AI Recruiter', airecAccess: true },
  // Where is my candidate? Its own row, not a Talent Hub tab (owner, 6 Jun
  // 2026): Talent Hub is for FINDING people, this is for tracking the ones
  // already sent. A recruiter's most-asked question deserves one click.
  { to: '/app/submissions', icon: Send, label: 'Submissions', hideBde: true, labelFor: { recruiter: 'My Submissions' } },
  // Owner's spec: Clients is core for managers, directors, admins and BDEs.
  // A RECRUITER only gets the row when they actually own an account — working
  // a client's role is not client access (backend: get_visible_client_ids).
  { to: '/app/clients', icon: Building2, label: 'Clients', bdeVisible: true, recruiterOwnsClient: true },
  // Calendar became Interviews on 6 Jun 2026 (owner): a candidate interview and
  // an internal meeting are different jobs with different audiences. A BDE
  // never sees Interviews — they get Meetings.
  { to: '/app/interviews', icon: Calendar, label: 'Interviews', hideBde: true },
  { to: '/app/meetings', icon: CalendarDays, label: 'Meetings', bdeVisible: true },
  { to: '/app/email-logs', icon: Mail, label: 'Email Logs', bdeVisible: true, labelFor: { recruiter: 'My Emails', bde: 'My Emails' } },
  // "Team" was manager-only, so a recruiter had no way to reach the page that
  // shows their own targets, progress and day notes — the page already rendered
  // a recruiter view. Renamed Performance because that word is true for every
  // persona: a recruiter's own numbers, a manager's team, a director's tree.
  // Sits in WORK, not Manage, because checking it is daily work for everyone.
  { to: '/app/team', icon: Target, label: 'Performance', hideBde: true },
  { to: '/app/business-dev', icon: Handshake, label: 'Business Dev', bdeVisible: true, bdAccess: true },

  /* ── MANAGE: Recruiting Managers, Directors, Admins ───────────────────── */
  { divider: true, label: 'Manage' },
  // People left Performance on 5 Sep 2026 (owner: "people settings should not
  // sit inside Performance"). A manager sees their own reporting line only.
  { to: '/app/people', icon: Contact, label: 'People', managerOnly: true },
  { to: '/app/reports', icon: FileBarChart, label: 'Reports', managerOnly: true },
  { to: '/app/sourcing-activity', icon: Activity, label: 'Sourcing Activity', managerOnly: true, hideBde: true },
  // Queue Health: the only screen that says whether the nightly dealers ran,
  // what is banked up and by how long, and whether the morning nudge landed.
  { to: '/app/queue-health', icon: ListChecks, label: 'Queue Health', managerOnly: true, hideBde: true },
  // Owner's call: the Owner Dashboard is for the owner/admin only — a
  // Director of Recruiting does not get it.
  { to: '/app/owner-dashboard', icon: Crown, label: 'Owner Dashboard', adminOnly: true },
  // Control Centre replaces the fourteen-row Admin block (25 Sep 2026, owner):
  // every setting is explained and reachable from one screen, and every old
  // address still works. Owner Dashboard stays separate — "how is the business
  // doing" is a different question from "how is the app configured".
  // A director holds the sourcing credit controls that live in here, so they
  // get the row too (Jun 2026) — the permission existed without a route to it.
  { to: '/app/control-centre', icon: SlidersHorizontal, label: 'Control Centre', directorUp: true },

  /* ── ADMIN: one entry, expanded on purpose (see the Admin row). ────────── */
  { divider: true, label: 'Admin', adminOnly: true, adminGroup: true, inControlCentre: true },
  { to: '/app/settings', icon: Settings, label: 'Settings', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/admin/security', icon: Lock, label: 'Integration Keys', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/scoring-engine', icon: Gauge, label: 'Scoring Engine', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/website', icon: Globe, label: 'Website', cmsAccess: true, hideBde: true, nonAdminOnly: true },
  { to: '/app/marketing-analytics', icon: BarChart3, label: 'Marketing & SEO', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/product-analytics', icon: BarChart3, label: 'App Usage', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/admin-tools', icon: Wrench, label: 'Admin Tools', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/admin', icon: LayoutGrid, label: 'Compliance Hub', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/admin/compliance', icon: Shield, label: 'Privacy Settings', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/admin/dsar', icon: Inbox, label: 'DSAR Queue', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/admin/data-health', icon: Activity, label: 'Data Health', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/admin/data-hygiene', icon: Wrench, label: 'Data Hygiene', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/admin/salary-repair', icon: IndianRupee, label: 'Salary Repair', inControlCentre: true, adminOnly: true, adminGroup: true },
  { to: '/app/admin/share-grants', icon: Shield, label: 'Share Admin Access', inControlCentre: true, adminOnly: true, adminGroup: true },
  // What stops at the admin's own login, in one table (28 Sep 2026, owner).
  { to: '/app/admin/admin-only', icon: Lock, label: 'Admin only', inControlCentre: true, adminOnly: true, adminGroup: true },

  /* ── PLATFORM: superadmin only, entered deliberately. ─────────────────── */
  { divider: true, label: 'Client ATS (Platform)', superadminOnly: true, platformGroup: true },
  { to: '/app/platform/workspaces', icon: Building2, label: 'Workspaces', superadminOnly: true, platformGroup: true },
  { to: '/app/platform/hirobase-leads', icon: Inbox, label: 'Hirobase Leads', superadminOnly: true, platformGroup: true },
  { to: '/app/platform/usage', icon: BarChart3, label: 'Platform Usage', superadminOnly: true, platformGroup: true },
  { to: '/app/platform/audit', icon: Shield, label: 'Platform Audit', superadminOnly: true, platformGroup: true },

  /* ── PARKED: still routed, just not in the sidebar. ───────────────────────
     Notifications = the top-bar bell. What's New = the top-bar sparkle.
     Learning = the top-bar help button. Account Management merged into
     Clients. Manager Performance is a Team tab. The rest are switched-off
     features kept for a deliberate switch-on. */
  { to: '/app/notifications', icon: Bell, label: 'Notifications', badge: 'notif', parked: true },
  { to: '/app/whats-new', icon: Sparkles, label: "What's New", badge: 'whatsnew', parked: true },
  { to: '/app/learning', icon: GraduationCap, label: 'Learning', parked: true },
  { to: '/app/my-clients', icon: Building2, label: 'Account Management', parked: true },
  { to: '/app/manager-performance', icon: Crown, label: 'Manager Performance', parked: true },
  { to: '/app/ai-recruiter', icon: Bot, label: 'HiroX', parked: true },
  { to: '/app/ai-autodialer', icon: Phone, label: 'AI Autodialer', parked: true },
  { to: '/app/bizx', icon: Zap, label: 'BizX', parked: true },
  { to: '/app/outreach', icon: Send, label: 'Reachout', parked: true },
];

/* ─── Sidebar Navigation ─── */
const SidebarNav = ({ onItemClick, collapsed = false }) => {
  const { user, logout, adminGrants } = useAuth();
  // "Show what's admin-only" — off by default, remembered on the admin's own
  // record so the answer is the same on the next machine.
  const adminHighlight = useAdminHighlight();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { themeName, setTheme, live, setLive, liveSlot, liveNextChange, motion, setMotion, isAnimated } = useTheme();
  const [showThemes, setShowThemes] = useState(false);
  useEffect(() => {
    if (!showThemes) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowThemes(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showThemes]);
  // The Admin and Platform blocks replace the old "Show all admin links"
  // footer toggle: each is one row you open on purpose. Auto-opens when you
  // land on one of its pages (bookmarks keep working and highlight correctly).
  const onAdminPath = /^\/app\/(settings|admin|admin-tools|scoring-engine|website|marketing-analytics|product-analytics)(\/|\?|$)/.test(routerLocation.pathname);
  const onPlatformPath = routerLocation.pathname.startsWith('/app/platform');
  const [showAdminGroup, setShowAdminGroup] = useState(() => {
    try { return localStorage.getItem('sidebar:adminGroup') === '1'; } catch { return false; }
  });
  const [showPlatformGroup, setShowPlatformGroup] = useState(false);
  useEffect(() => { if (onAdminPath) setShowAdminGroup(true); }, [onAdminPath]);
  useEffect(() => { if (onPlatformPath) setShowPlatformGroup(true); }, [onPlatformPath]);
  const toggleAdminGroup = () => {
    setShowAdminGroup(v => {
      const next = !v;
      try { localStorage.setItem('sidebar:adminGroup', next ? '1' : '0'); } catch (_) { void _; }
      return next;
    });
  };
  const [featureFlags, setFeatureFlags] = useState({});
  const [openQueryCount, setOpenQueryCount] = useState(0);
  const [myClientsCount, setMyClientsCount] = useState(0);
  const [whatsNewCount, setWhatsNewCount] = useState(0);
  const [pulseCount, setPulseCount] = useState(0);
  const [airec, setAirec] = useState(null);
  useEffect(() => {
    if (!user) return undefined;
    const load = () => api.get('/airec/me').then(r => setAirec(r.data)).catch(() => setAirec(null));
    load();
    const t = setInterval(load, 120000);
    window.addEventListener('airec:changed', load);
    return () => { clearInterval(t); window.removeEventListener('airec:changed', load); };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [hasAssignedClients, setHasAssignedClients] = useState(false);
  const [ownsClients, setOwnsClients] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const profileRef = useRef(null);

  const boot = useBootstrap(!!user);
  useEffect(() => {
    if (!boot.data) return;
    setFeatureFlags(boot.data.feature_flags || {});
    setWhatsNewCount(boot.data.feature_updates_unread || 0);
    setOpenQueryCount(boot.data.open_portal_queries || 0);
    setPulseCount(boot.data.pulse_count?.total || 0);
    setMyClientsCount(boot.data.my_clients_unread || 0);
    const clients = boot.data.my_clients || [];
    setHasAssignedClients(Array.isArray(clients) ? clients.length > 0
      : (boot.data.my_clients_unread || 0) > 0);
    // Account ownership, not job involvement — the recruiter Clients gate.
    setOwnsClients(Array.isArray(clients) && clients.some(c => c.is_account_manager));
  }, [boot.data]);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filterItem = (item, idx, arr) => {
    const isBde = user?.role === 'bde';
    // Platform Console visible if the caller is superadmin OR an admin has
    // granted them the `platform_settings` capability. In view-as mode the
    // AuthProvider suppresses grants + strips is_superadmin, so the persona
    // sees exactly what the target role sees.
    // Owner's rule: nobody outside a real superadmin ever SEES the Platform
    // switch. A temporary `platform_settings` grant still opens the routes for
    // an admin who has the URL, it just isn't advertised in the nav.
    const isSuperadmin = !!user?.is_superadmin;
    if (item.divider) {
      if (collapsed) return false;
      if (item.adminGroup) return false;   // folded into the Control Centre
      if (item.platformGroup) return isSuperadmin;
      const nextItems = arr.slice(idx + 1);
      const nextDividerIdx = nextItems.findIndex(i => i.divider);
      const sectionItems = nextDividerIdx >= 0 ? nextItems.slice(0, nextDividerIdx) : nextItems;
      return sectionItems.some(i => {
        // Parked sections (see the Outreach block) never render a divider.
        if (i.parked) return false;
        // Hidden-by-default items don't keep their section divider visible.
        if (i.hideByDefault) return false;
        if (i.superadminOnly) return isSuperadmin;
        if (i.airecAccess) return !!airec?.enabled || airec?.assigned_count > 0;
        if (i.bdeOnly) return isBde;
        if (isBde) return !!i.bdeVisible;
        if (i.hideBde && isBde) return false;
        if (i.recruiterOwnsClient && user?.role === 'recruiter') return ownsClients;
        if (i.showIfAssigned) return hasAssignedClients;
        if (i.cmsAccess) {
          if (user?.role === 'admin') return true;
          if (user?.role !== 'director') return false;
          return !!(user?.can_manage_cms || featureFlags['director_cms_access']
            || featureFlags['manager_cms_access']);
        }
        if (i.adminOnly && !i.featureFlag) return user?.role === 'admin';
        if (i.adminOnly && i.featureFlag) {
          if (user?.role === 'admin') return true;
          // ai_recruiter_access only unlocks ai_recruiter_visible, not other feature-flagged items
          if (i.featureFlag === 'ai_recruiter_visible') return featureFlags[i.featureFlag] || user?.ai_recruiter_access;
          // For other admin+featureFlag items (e.g. sourcing_activity), require explicit user-level grant
          const permKey = `can_view_${i.featureFlag.replace('_visible', '')}`;
          return user?.[permKey] || false;
        }
        if (i.bdAccess) return hasBdAccess(user);
        if (i.directorUp) return ['admin', 'director'].includes(user?.role);
        if (i.managerOrAssigned) return ['admin', 'director', 'manager'].includes(user?.role) || hasAssignedClients;
        if (i.managerOnly) return ['admin', 'director', 'manager'].includes(user?.role);
        if (i.featureFlag) return featureFlags[i.featureFlag] !== false;
        return true;
      });
    }
    // Parked features (see the Outreach block above) are hidden for EVERY role,
    // including admins. Checked first so no role rule can override it.
    if (item.parked) return false;
    // Folded into the Control Centre (25 Sep 2026): the screen still exists at
    // its own address and still works from a bookmark, it is simply reached
    // through the Control Centre instead of its own nav row.
    if (item.inControlCentre) return false;
    // An admin reaches the Website through the Control Centre; a CMS editor who
    // is not an admin still needs the row.
    if (item.nonAdminOnly && user?.role === 'admin') return false;
    // The Admin and Platform blocks are collapsed until their row is opened.
    if (item.adminGroup && !item.divider && !showAdminGroup) return false;
    if (item.platformGroup && !item.divider && !showPlatformGroup) return false;
    if (item.airecAccess) return !!airec?.enabled || airec?.assigned_count > 0;
    if (item.bdeOnly) return isBde;
    if (isBde) return !!item.bdeVisible;
    // Tucked-away by default — power users can flip via "Show all admin links".
    if (item.hideByDefault) return false;
    if (item.superadminOnly) return isSuperadmin;
    if (item.recruiterOwnsClient && user?.role === 'recruiter') return ownsClients;
    if (item.showIfAssigned) return hasAssignedClients;
    if (item.cmsAccess) {
      if (user?.role === 'admin') return true;
      if (user?.role !== 'director') return false;
      return !!(user?.can_manage_cms || featureFlags['director_cms_access']
        || featureFlags['manager_cms_access']);
    }
    if (item.adminOnly && !item.featureFlag) return user?.role === 'admin';
    if (item.adminOnly && item.featureFlag) {
      if (user?.role === 'admin') return true;
      if (item.featureFlag === 'ai_recruiter_visible') return featureFlags[item.featureFlag] || user?.ai_recruiter_access;
      const permKey = `can_view_${item.featureFlag.replace('_visible', '')}`;
      return user?.[permKey] || false;
    }
    if (item.bdAccess) return hasBdAccess(user);
    if (item.directorUp) return ['admin', 'director'].includes(user?.role);
    if (item.managerOrAssigned) return ['admin', 'director', 'manager'].includes(user?.role) || hasAssignedClients;
    if (item.managerOnly) return ['admin', 'director', 'manager'].includes(user?.role);
    if (item.featureFlag) return featureFlags[item.featureFlag] !== false;
    return true;
  };

  const visibleNavItems = useMemo(() => navItems.filter(filterItem), [user, collapsed, hasAssignedClients, ownsClients, featureFlags, showAdminGroup, showPlatformGroup, airec]);
  // Owner's call (Phase 1): only four themes are OFFERED — dark default, dark
  // alt, light and high-contrast. The other 15 stay in the code, reachable by
  // flipping SHOW_ALL_THEMES, and anyone already on one keeps it.
  const shippedOnly = ([key, t]) => SHOW_ALL_THEMES || t.shipped || key === themeName;
  // Live themes get their own section — they are a set, not six loose options.
  const darkThemes = useMemo(() => Object.entries(themes).filter(([k, t]) => !t.hidden && !t.live && t.group === 'dark' && shippedOnly([k, t])), [themeName]);
  const lightThemes = useMemo(() => Object.entries(themes).filter(([k, t]) => !t.hidden && !t.live && t.group === 'light' && shippedOnly([k, t])), [themeName]);
  // Live scenes are listed in two sections rather than one: half of them are
  // light and half dark, and someone hunting for "a scene I can use at 10am"
  // should not have to know which is which (owner, 6 Jun 2026).
  const liveDayThemes = useMemo(() => Object.entries(themes).filter(([k, t]) => !t.hidden && t.live && t.group === 'light'), []);
  const liveNightThemes = useMemo(() => Object.entries(themes).filter(([k, t]) => !t.hidden && t.live && t.group === 'dark'), []);

  return (
    <div className="flex flex-col h-full">
      {/* ── Logo ── */}
      <div className={`flex items-center ${collapsed ? 'p-4 justify-center' : 'px-4 py-4'} ${
        themes[themeName]?.lightSidebar ? 'brand-on-light' : 'brand-on-dark'}`}>
        {/* The supplied lockup is VERTICAL (rocket stacked above the wordmark).
            At sidebar height the wordmark inside it renders about 6px tall and
            is unreadable, so expanded shows the mark plus the name set in the
            app's own type. Collapsed is the mark alone — never the tall lockup
            squeezed into a square. */}
        {collapsed ? (
          <BrandMark className="h-7 w-7" testId="sidebar-logo" />
        ) : (
          <BrandHorizontal className="h-8 w-auto" testId="sidebar-logo" />
        )}
      </div>

      {/* ── Workspace switcher (multi-tenant) ── */}
      <WorkspaceSwitcher collapsed={collapsed} />

      {/* ── Nav Items ── */}
      <div className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden ${collapsed ? 'px-2 py-3' : 'px-3 py-2'}`}
        style={{ scrollbarWidth: 'none' }}>
        <nav className={collapsed ? 'flex flex-col items-center gap-1' : 'space-y-1'} data-testid="sidebar-nav">
          {visibleNavItems.map((item, idx) => (
            // The Admin / Platform dividers are clickable rows that open their
            // block; the Manage divider is a plain section label.
            item.divider && (item.adminGroup || item.platformGroup) ? (
              <button
                key={`group-${idx}`}
                onClick={item.adminGroup ? toggleAdminGroup : () => setShowPlatformGroup(v => !v)}
                {...(item.adminGroup ? { 'data-admin-only': 'true' } : {})}
                data-testid={item.adminGroup ? 'nav-admin-group' : 'nav-platform-group'}
                className="w-full flex items-center gap-3 px-3 py-2 mt-4 rounded-ui text-body font-medium text-ink-2 hover:text-ink-1 hover:bg-surface-2 transition-colors"
              >
                {item.adminGroup ? <Settings className="w-[18px] h-[18px]" /> : <Building2 className="w-[18px] h-[18px]" />}
                <span>{item.adminGroup ? 'Admin' : 'Switch to Platform'}</span>
                {item.adminGroup && <AdminOnlyMark className="-ml-1" />}
                <ChevronDown className={`ml-auto w-3.5 h-3.5 transition-transform ${
                  (item.adminGroup ? showAdminGroup : showPlatformGroup) ? '' : '-rotate-90'
                }`} />
              </button>
            ) : item.divider ? (
              <div key={`divider-${idx}`} className="pt-4 pb-2">
                <p className="px-3 text-caption font-medium tracking-[0.06em] uppercase text-ink-3">
                  {item.labelFor?.[user?.role] || item.label}
                </p>
              </div>
            ) : collapsed ? (
              <TooltipProvider key={item.to} delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.to}
                      onClick={onItemClick}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={({ isActive }) =>
                        `relative flex items-center justify-center w-10 h-10 rounded-ui-lg transition-colors duration-200 ${
                          isActive
                            ? 'text-sidebar-1 bg-sidebar-sel'
                            : 'text-sidebar-3 hover:text-sidebar-1 hover:bg-sidebar-hover'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-brand" />
                          )}
                          <item.icon className="w-[18px] h-[18px]" />
                          {item.to === '/app/client-portal' && openQueryCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] flex items-center justify-center text-[7px] font-bold text-brand-ink bg-brand rounded-full px-1">
                              {openQueryCount}
                            </span>
                          )}
                          {item.to === '/app/clients' && myClientsCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] flex items-center justify-center text-[7px] font-bold text-brand-ink bg-status-bad rounded-full px-1" data-testid="my-clients-badge-collapsed">
                              {myClientsCount}
                            </span>
                          )}
                          {item.to === '/app/dashboard' && pulseCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] flex items-center justify-center text-[7px] font-bold text-brand-ink bg-brand rounded-full px-1" data-testid="pulse-nav-badge-collapsed">
                              {pulseCount}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}
                    className="bg-sidebar-pop border-sidebar-line text-sidebar-1 text-caption font-medium px-3 py-1 rounded-ui shadow-e1">
                    {item.labelFor?.[user?.role] || item.label}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onItemClick}
                {...(item.adminOnly ? { 'data-admin-only': 'true' } : {})}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 px-3 py-2 rounded-ui-lg text-body font-medium transition-colors duration-200 ${
                    isActive
                      ? 'text-sidebar-1 bg-sidebar-sel'
                      : 'text-sidebar-2 hover:text-sidebar-1 hover:bg-sidebar-hover'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-brand" />
                    )}
                    <item.icon className={`w-[18px] h-[18px] transition-colors duration-200 ${isActive ? 'text-brand' : 'text-sidebar-3 group-hover:text-sidebar-1'}`} />
                    <span>{item.labelFor?.[user?.role] || item.label}</span>
                    {/* Only an admin is told which rows are theirs alone. */}
                    {item.adminOnly && !item.adminGroup && <AdminOnlyMark className="-ml-1" />}
                    {item.to === '/app/client-portal' && openQueryCount > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center text-caption font-bold text-brand-ink bg-brand rounded-full px-1">
                        {openQueryCount}
                      </span>
                    )}
                    {item.to === '/app/clients' && myClientsCount > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center text-caption font-bold text-brand-ink bg-status-bad rounded-full px-1" data-testid="my-clients-badge">
                        {myClientsCount}
                      </span>
                    )}
                    {item.to === '/app/whats-new' && whatsNewCount > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center text-caption font-bold text-brand-ink bg-status-info rounded-full px-1" data-testid="whats-new-badge">
                        {whatsNewCount}
                      </span>
                    )}
                    {item.to === '/app/ai-recruit' && airec?.inbox_count > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center text-caption font-bold text-brand-ink bg-status-warn rounded-full px-1" data-testid="airec-nav-badge">
                        {airec.inbox_count}
                      </span>
                    )}
                    {item.to === '/app/dashboard' && pulseCount > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center text-caption font-bold text-brand-ink bg-brand rounded-full px-1" data-testid="pulse-nav-badge">
                        {pulseCount}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            )
          ))}
        </nav>
      </div>

      {/* ── Bottom Section ── */}
      <div className={`border-t border-sidebar-line ${collapsed ? 'p-2' : 'p-3'}`}>
        {/* Environment Badge — only shows in non-production */}
        {!collapsed && window.location.hostname.includes('preview.emergentagent.com') && (
          <div className="mb-2 mx-1 px-3 py-1 bg-status-warn-soft rounded-ui flex items-center gap-2" data-testid="env-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-status-warn" />
            <span className="text-caption font-semibold tracking-wider text-status-warn uppercase">Staging</span>
          </div>
        )}
        {collapsed && window.location.hostname.includes('preview.emergentagent.com') && (
          <div className="mb-2 mx-auto w-8 h-8 bg-status-warn-soft rounded-ui flex items-center justify-center" title="Staging Environment" data-testid="env-badge-collapsed">
            <span className="text-caption font-semibold text-status-warn">STG</span>
          </div>
        )}
        {/* Theme. Collapsed shows the palette icon alone: with the sidebar now
            starting collapsed on small laptops, hiding it entirely would have
            taken the theme picker away from exactly those recruiters. */}
        <button
          onClick={() => setShowThemes(!showThemes)}
          title={collapsed ? 'Theme' : undefined}
          className={`flex items-center gap-2 py-2 mb-1 rounded-ui-lg text-meta text-sidebar-3 hover:text-sidebar-1 hover:bg-sidebar-hover transition-colors duration-200 ${
            collapsed ? 'mx-auto w-9 justify-center px-0' : 'w-full px-3'}`}
          data-testid="theme-picker-toggle"
        >
          <Palette className="w-4 h-4 shrink-0" />
          {!collapsed && (
            <>
              <span>Theme</span>
              <span className={`ml-auto w-2.5 h-2.5 rounded-full ${themes[themeName]?.dot}`} />
            </>
          )}
        </button>
        {showThemes && (
          /* Scroll-capped: with fourteen live scenes plus the fixed palettes the
             list is taller than a laptop sidebar. Collapsed, it floats out to
             the right of the 64px rail instead of trying to fit inside it. */
          <div className={`bg-sidebar-hover border border-sidebar-line rounded-ui-lg max-h-[62svh] overflow-y-auto ${
            collapsed ? 'absolute left-[68px] bottom-20 w-60 z-50 p-1 shadow-2xl' : 'mb-2 mx-1 p-1'}`} data-testid="theme-options">
            {/* ── Live mode. Off by default; picking any theme by hand turns it
                off again, so this can never fight a deliberate choice. ── */}
            <button
              onClick={() => setLive(!live)}
              className={`w-full flex items-start gap-2 px-3 py-2 rounded-ui text-caption text-left transition-colors duration-150 ${
                live ? 'bg-sidebar-sel text-sidebar-1' : 'text-sidebar-3 hover:text-sidebar-1 hover:bg-sidebar-hover'
              }`}
              data-testid="theme-live-toggle"
              aria-pressed={live}
              title="Follows the time of day: sunrise, morning, afternoon, sunset, evening, night"
            >
              <Sparkles className={`w-3.5 h-3.5 mt-1 shrink-0 ${live ? 'text-sidebar-1' : ''}`} />
              <span className="min-w-0">
                <span className="block font-medium">Live theme {live ? 'on' : 'off'}</span>
                <span className="block text-sidebar-3" data-testid="theme-live-status">
                  {live
                    ? `${liveSlot.label} · ${themes[themeName]?.name} until ${liveNextChange}`
                    : 'Follow the time of day'}
                </span>
              </span>
              <span className={`ml-auto mt-1 w-8 h-4 rounded-full shrink-0 border transition-colors ${
                live ? 'bg-sidebar-sel border-sidebar-1' : 'bg-sidebar-hover border-sidebar-line'}`}>
                <span className={`block w-3 h-3 mt-px rounded-full transition-transform ${
                  live ? 'translate-x-[18px] bg-sidebar-1' : 'translate-x-[2px] bg-sidebar-3'}`} />
              </span>
            </button>

            {/* Motion. An animated theme with the animation off is just a
                palette, so this stays ON by default — but a moving background
                is not for everyone, and the OS `prefers-reduced-motion` wins
                regardless. Only shown when the current theme actually moves. */}
            {isAnimated && (
              <button
                onClick={() => setMotion(!motion)}
                className={`w-full flex items-center gap-2 px-3 py-1 mt-1 rounded-ui text-caption text-left transition-colors duration-150 ${
                  motion ? 'text-sidebar-1' : 'text-sidebar-3 hover:text-sidebar-1 hover:bg-sidebar-hover'
                }`}
                data-testid="theme-motion-toggle"
                aria-pressed={motion}
                title="Stop the background moving without changing the colours"
              >
                <Waves className="w-3.5 h-3.5 shrink-0" />
                <span>Motion {motion ? 'on' : 'off'}</span>
                <span className={`ml-auto w-8 h-4 rounded-full shrink-0 border transition-colors ${
                  motion ? 'bg-sidebar-sel border-sidebar-1' : 'bg-sidebar-hover border-sidebar-line'}`}>
                  <span className={`block w-3 h-3 mt-px rounded-full transition-transform ${
                    motion ? 'translate-x-[18px] bg-sidebar-1' : 'translate-x-[2px] bg-sidebar-3'}`} />
                </span>
              </button>
            )}

            <div className="px-2 py-1 mt-1 text-caption uppercase tracking-wider text-sidebar-3 font-medium border-t border-sidebar-line pt-2">Live · day</div>
            {liveDayThemes.map(([key, t]) => (
              <button
                key={key}
                onClick={() => { setTheme(key); setShowThemes(false); }}
                className={`w-full flex items-center gap-2 px-3 py-1 rounded-ui text-caption transition-colors duration-150 ${
                  themeName === key ? 'bg-sidebar-sel text-sidebar-1' : 'text-sidebar-3 hover:text-sidebar-1 hover:bg-sidebar-hover'
                }`}
                data-testid={`theme-${key}`}
                title={t.blurb}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${t.dot}`} />
                {t.name}
                {live && themeName === key && <span className="ml-auto text-sidebar-3">now</span>}
              </button>
            ))}
            <div className="px-2 py-1 mt-1 text-caption uppercase tracking-wider text-sidebar-3 font-medium border-t border-sidebar-line pt-2">Live · night</div>
            {liveNightThemes.map(([key, t]) => (
              <button
                key={key}
                onClick={() => { setTheme(key); setShowThemes(false); }}
                className={`w-full flex items-center gap-2 px-3 py-1 rounded-ui text-caption transition-colors duration-150 ${
                  themeName === key ? 'bg-sidebar-sel text-sidebar-1' : 'text-sidebar-3 hover:text-sidebar-1 hover:bg-sidebar-hover'
                }`}
                data-testid={`theme-${key}`}
                title={t.blurb}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${t.dot}`} />
                {t.name}
                {live && themeName === key && <span className="ml-auto text-sidebar-3">now</span>}
              </button>
            ))}
            <div className="px-2 py-1 mt-1 text-caption uppercase tracking-wider text-sidebar-3 font-medium border-t border-sidebar-line pt-2">Night</div>
            {darkThemes.map(([key, t]) => (
              <button
                key={key}
                onClick={() => { setTheme(key); setShowThemes(false); }}
                className={`w-full flex items-center gap-2 px-3 py-1 rounded-ui text-caption transition-colors duration-150 ${
                  themeName === key ? 'bg-sidebar-sel text-sidebar-1' : 'text-sidebar-3 hover:text-sidebar-1 hover:bg-sidebar-hover'
                }`}
                data-testid={`theme-${key}`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${t.dot}`} />
                {t.name}
              </button>
            ))}
            <div className="px-2 py-1 mt-1 text-caption uppercase tracking-wider text-sidebar-3 font-medium border-t border-sidebar-line pt-2">Light</div>
            {lightThemes.map(([key, t]) => (
              <button
                key={key}
                onClick={() => { setTheme(key); setShowThemes(false); }}
                className={`w-full flex items-center gap-2 px-3 py-1 rounded-ui text-caption transition-colors duration-150 ${
                  themeName === key ? 'bg-sidebar-sel text-sidebar-1' : 'text-sidebar-3 hover:text-sidebar-1 hover:bg-sidebar-hover'
                }`}
                data-testid={`theme-${key}`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${t.dot}`} />
                {t.name}
              </button>
            ))}
          </div>
        )}

        {/* View-as role switcher (admins only; hidden when collapsed) */}
        {!collapsed && <ViewAsSwitcher onNavigate={onItemClick} />}

        {/* Profile Card */}
        <div ref={profileRef} className="relative">
          {collapsed ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => navigate('/app/profile')}
                    className="w-10 h-10 mx-auto rounded-ui-lg flex items-center justify-center hover:bg-sidebar-hover transition-colors"
                    data-testid="sidebar-profile-link"
                  >
                    <Avatar className="w-7 h-7">
                      <AvatarFallback className="bg-brand-soft text-brand text-caption font-semibold">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8} className="bg-sidebar-pop border-sidebar-line text-sidebar-1 text-caption rounded-ui">
                  {user?.name}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <>
              <button
                onClick={() => setShowProfile(!showProfile)}
                className="w-full flex items-center gap-3 px-2 py-2 rounded-ui-lg hover:bg-sidebar-hover transition-colors duration-200"
                data-testid="sidebar-profile-link"
              >
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-brand-soft text-brand text-caption font-semibold">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-body font-medium text-sidebar-1 truncate leading-tight">{user?.name}</p>
                  <p className="text-caption text-sidebar-3 truncate">{user?.email}</p>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-sidebar-3 transition-transform duration-200 ${showProfile ? 'rotate-180' : ''}`} />
              </button>

              {/* Profile Dropdown */}
              {showProfile && (
                <div className="absolute bottom-full left-0 right-0 mb-1 mx-1 bg-sidebar-pop border border-sidebar-line rounded-ui-lg shadow-e2 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-150">
                  <div className="px-3 py-2 border-b border-sidebar-line">
                    <div className="flex items-center gap-2">
                      <p className="text-caption font-medium text-sidebar-1">{user?.name}</p>
                      <span className={`text-caption uppercase tracking-wider font-semibold px-1 py-1 rounded-ui ${
                        user?.role === 'admin' ? 'bg-status-bad-soft text-status-bad' : user?.role === 'bde' ? 'bg-status-warn-soft text-status-warn' : 'bg-brand-soft text-brand'
                      }`}>{user?.role === 'bde' ? 'BDE' : user?.role}</span>
                    </div>
                    <p className="text-caption text-sidebar-3 mt-1">{user?.email}</p>
                  </div>
                  <div className="py-1">
                    <button onClick={() => { setShowProfile(false); onItemClick?.(); navigate('/app/profile'); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-caption text-sidebar-2 hover:text-sidebar-1 hover:bg-sidebar-hover transition-colors">
                      <Users className="w-3.5 h-3.5" /> Profile
                    </button>
                    {adminHighlight.isAdmin && (
                      <button onClick={adminHighlight.toggle} data-admin-only="true"
                        className="w-full flex items-center gap-2 px-3 py-2 text-caption text-sidebar-2 hover:text-sidebar-1 hover:bg-sidebar-hover transition-colors"
                        data-testid="admin-highlight-toggle">
                        <Lock className="w-3.5 h-3.5" />
                        <span className="flex-1 text-left">Show what&apos;s admin-only</span>
                        <span className={adminHighlight.on ? 'text-brand' : 'text-sidebar-3'}>
                          {adminHighlight.on ? 'On' : 'Off'}
                        </span>
                      </button>
                    )}
                    <button onClick={() => { setShowProfile(false); onItemClick?.(); navigate('/app/settings/change-password'); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-caption text-sidebar-2 hover:text-sidebar-1 hover:bg-sidebar-hover transition-colors"
                      data-testid="change-password-btn">
                      <Settings className="w-3.5 h-3.5" /> Change Password
                    </button>
                    <div className="border-t border-sidebar-line mt-1 pt-1">
                      <button onClick={() => { logout(); navigate('/teamlogin'); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-caption text-status-bad hover:bg-status-bad-soft transition-colors"
                        data-testid="logout-btn">
                        <LogOut className="w-3.5 h-3.5" /> Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {collapsed && (
            <button
              className="w-10 h-10 mx-auto mt-1 rounded-ui-lg flex items-center justify-center text-sidebar-3 hover:text-status-bad hover:bg-status-bad-soft transition-colors"
              onClick={() => { logout(); navigate('/teamlogin'); }}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Layout ─── */
export const Layout = () => {
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  /* First run only: a 1366 or 1440 laptop starts collapsed, a big monitor does
     not. This is the ONE legitimate width read in the app — a one-time
     preference default via matchMedia (CSS px, so 125% scaling counts), never
     per-frame layout. The moment a human touches the toggle their choice is
     written and wins on every screen, forever. */
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    if (saved === 'true') return true;
    if (saved === 'false') return false;
    return typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 1500px)').matches;
  });
  /* Collapsed is never a dead end: hovering the rail flies the labels out over
     the page without moving the page. */
  const [hoverPeek, setHoverPeek] = useState(false);

  // Real-time sync: SSE connection for live updates from other recruiters
  useRealtimeSync(true);

  // Product analytics: auto-track page views on route changes
  usePageTracker();

  const toggleCollapse = () => {
    setCollapsed(prev => {
      localStorage.setItem('sidebar_collapsed', !prev ? 'true' : 'false');
      return !prev;
    });
  };

  const pageWidth = pageWidthFor(routerLocation.pathname);
  const isFill = FILL_ROUTES.has(routerLocation.pathname);
  const peeking = collapsed && hoverPeek;
  const sidebarWidth = collapsed && !peeking ? 'w-16' : 'w-[264px]';
  // The page's margin follows the COLLAPSED state, not the peek — a fly-out
  // that shoved the whole table sideways would be worse than no labels.
  const mainMargin = collapsed ? 'lg:ml-16' : 'lg:ml-[264px]';

  return (
    /* One flex column, exactly one viewport tall. `dvh` (not vh) so a phone's
       collapsing toolbar and a Mac's dynamic chrome can never crop the page. */
    <div className="app-shell h-[100dvh] flex flex-col overflow-hidden bg-obsidian">
      {/* The animated nature layer. Fixed, behind everything, pointer-events
          none — see components/ThemeAmbience.jsx. Renders nothing unless the
          active theme declares an `ambience`. */}
      <ThemeAmbience />
      <TopLoader />
      <CommandPalette />
      <GuidedTour />

      {/* Sidebar (desktop) */}
      <aside
        className={`${sidebarWidth} fixed h-[100dvh] bg-sidebar border-r border-sidebar-line z-50 hidden lg:flex flex-col transition-all duration-300 ease-sidebar ${peeking ? 'shadow-2xl' : ''}`}
        data-testid="sidebar"
        onMouseEnter={() => collapsed && setHoverPeek(true)}
        onMouseLeave={() => setHoverPeek(false)}
      >
        <SidebarNav collapsed={collapsed && !peeking} />
        <button
          onClick={toggleCollapse}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-sidebar-pop border border-sidebar-line shadow-e1 flex items-center justify-center text-sidebar-3 hover:text-sidebar-1 transition-colors duration-200 z-50"
          data-testid="sidebar-collapse-btn"
        >
          {collapsed ? <PanelLeft className="w-3 h-3" /> : <PanelLeftClose className="w-3 h-3" />}
        </button>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden shrink-0 z-40 h-14 bg-sidebar border-b border-sidebar-line flex items-center px-4">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-sidebar-2" data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[264px] p-0 bg-sidebar border-sidebar-line">
            <SidebarNav onItemClick={() => setSheetOpen(false)} />
          </SheetContent>
        </Sheet>
        <span className="ml-3 flex items-center gap-2 flex-1 min-w-0">
          {/* The phone header had no logo at all — just the word. Auto ink: the
              header is `bg-sidebar`, light exactly in the light theme group. */}
          <BrandHorizontal className="h-6 w-auto" testId="mobile-header-logo" />
        </span>
        <WhatsNewButton mobile />
        <NotificationBell variant="mobile" />
      </div>

      {/* Main content */}
      {/* `relative z-[1]` is load-bearing: the ambience layer sits at z-index 0,
          and an unpositioned <main> would paint underneath it. */}
      <main className={`${mainMargin} relative z-[1] flex-1 min-h-0 flex flex-col overflow-x-hidden transition-all duration-300 ease-sidebar`} data-testid="main-content">
        <ViewAsBanner />
        <AiStatusBanner />
        {/* Top bar — a flex row of the shell, so whatever is left below it is
            the page's real height. Banners above shift it automatically. */}
        <div className="shrink-0 z-30 h-12 bg-surface-0/90 backdrop-blur-xl border-b border-line hidden lg:flex items-center justify-end px-6 gap-3 overflow-visible" data-testid="top-bar">
          {/* Short screens only: the page hands its title up here (see
              PageTitleSlot) and drops its own header row, which is worth ~44px
              of list on a 768px-tall laptop. Tall screens are untouched. */}
          <div id="page-title-slot" className="short-only mr-auto min-w-0 items-center gap-2" data-testid="page-title-slot" />
          <button
            onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }))}
            className="flex items-center gap-2 h-8 px-3 rounded-ui bg-surface-3 border border-line hover:bg-surface-2 transition-colors text-ink-3 hover:text-ink-1 text-caption"
            data-testid="cmd-k-hint-btn"
          >
            <Search className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">Search</span>
            <kbd className="ml-1 text-caption font-mono bg-surface-2 border border-line rounded-ui px-1 py-1">⌘K</kbd>
          </button>
          <button
            onClick={() => navigate('/app/learning')}
            title="Learning, research and help"
            data-testid="help-btn"
            className="h-8 w-8 flex items-center justify-center rounded-ui text-ink-3 hover:text-ink-1 hover:bg-surface-2 transition-colors"
          >
            <GraduationCap className="w-4 h-4" />
          </button>
          <DensityToggle />
          <WhatsNewButton />
          <NotificationBell variant="desktop" />
        </div>
        {/* Phase 2 frame: one gutter token, and a content width capped by page
            type so wide monitors stop stretching every row. The frame is the
            scroll container now — the page never fights the window for it. */}
        <div className={`page-frame flex-1 min-h-0 ${isFill ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          <div className={`${pageWidth} mx-auto w-full ${isFill ? 'page-fill flex flex-col' : ''}`}>
            <AnimatedPage fill={isFill}>
              <Outlet />
            </AnimatedPage>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Layout;
